/**
 * CompletionTrackingDashboard - Domain-agnostic completion/credential tracking.
 *
 * Uses CompletionManager queries (DRR-221) for efficient progress tracking:
 * - actorCompletions: What has each actor completed?
 * - completionGaps: Who still needs to complete required items?
 * - expiringCredentials: Whose credentials are expiring soon?
 * - hasCompleted: O(1) existence check
 *
 * This dashboard uses O(1) Valkey lookups for all completion checks.
 *
 * Domain-agnostic design (DRR-232):
 * - Healthcare: training modules, certifications, competencies
 * - Construction: OSHA certs, equipment licenses, safety cards
 * - Legal: CLE credits, bar membership, ethics training
 * - Finance: compliance training, SOX certs, AML courses
 *
 * Labels are resolved via useTerminology() from tenant settings.
 *
 * Route: /ferp-core/training/compliance
 * Scope: training:manage (supervisors/managers only)
 */

import { useState, useMemo } from 'react';
import {
  Container,
  Group,
  Stack,
  Title,
  ThemeIcon,
  Card,
  Text,
  Badge,
  Alert,
  Table,
  Progress,
  Tabs,
  Loader,
  ActionIcon,
  Tooltip,
  SegmentedControl,
} from '@mantine/core';
import {
  IconChartBar,
  IconAlertTriangle,
  IconClock,
  IconCheck,
  IconX,
  IconRefresh,
  IconUsers,
  IconCertificate,
} from '@tabler/icons-react';
import { useQuery } from '@apollo/client/react';
import {
  useExpiringCredentialsQuery,
  useCompletionGapsQuery,
  useActorCompletionsQuery,
  useActiveCredentialsQuery,
  useExpiredCredentialsQuery,
} from '@/generated/graphql';

// Domain-agnostic labels - fallbacks if terminology not configured
const DEFAULT_LABELS = {
  credential: 'Requirement',
  credentials: 'Requirements',
  expiring: 'Expiring Soon',
  expired: 'Expired',
  active: 'Active',
  gaps: 'Gaps',
  completed: 'Completed',
  actor: 'Person',
  actors: 'People',
};

/**
 * Expiring credentials section - shows items expiring within configurable days
 */
function ExpiringSection({ templateRef, withinDays = 30 }: { templateRef: string; withinDays?: number }) {
  const { data, loading, error, refetch } = useExpiringCredentialsQuery({
    variables: { templateRef, withinDays },
  });

  if (loading) return <Loader size="sm" />;
  if (error) return <Alert color="red" title="Error">{error.message}</Alert>;

  const items = data?.expiringCredentials ?? [];

  return (
    <Card withBorder padding="md">
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="orange">
            <IconClock size={14} />
          </ThemeIcon>
          <Text fw={500}>Expiring Within {withinDays} Days</Text>
        </Group>
        <Group gap="xs">
          <Badge color="orange" variant="light">{items.length}</Badge>
          <Tooltip label="Refresh">
            <ActionIcon variant="subtle" onClick={() => refetch()}>
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {items.length === 0 ? (
        <Text c="dimmed" size="sm">No items expiring soon</Text>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Person</Table.Th>
              <Table.Th>Expires</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((item, idx) => (
              <Table.Tr key={idx}>
                <Table.Td>{item.actorId}</Table.Td>
                <Table.Td>
                  <Badge color="orange" variant="light">
                    {new Date(item.expiresAt).toLocaleDateString()}
                  </Badge>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Card>
  );
}

/**
 * Completion gaps section - shows who still needs to complete required items
 */
function GapsSection({ templateRef, requiredActorIds }: { templateRef: string; requiredActorIds: string[] }) {
  const { data, loading, error, refetch } = useCompletionGapsQuery({
    variables: { templateRef, requiredActorIds },
    skip: requiredActorIds.length === 0,
  });

  if (loading) return <Loader size="sm" />;
  if (error) return <Alert color="red" title="Error">{error.message}</Alert>;

  const gaps = data?.completionGaps ?? [];
  const completedCount = requiredActorIds.length - gaps.length;
  const progressPercent = requiredActorIds.length > 0
    ? Math.round((completedCount / requiredActorIds.length) * 100)
    : 0;

  return (
    <Card withBorder padding="md">
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="red">
            <IconX size={14} />
          </ThemeIcon>
          <Text fw={500}>Completion Gaps</Text>
        </Group>
        <Group gap="xs">
          <Badge color={gaps.length > 0 ? 'red' : 'green'} variant="light">
            {gaps.length} remaining
          </Badge>
          <Tooltip label="Refresh">
            <ActionIcon variant="subtle" onClick={() => refetch()}>
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      <Progress
        value={progressPercent}
        color={progressPercent === 100 ? 'green' : progressPercent > 50 ? 'yellow' : 'red'}
        size="lg"
        mb="md"
      />
      <Text size="sm" c="dimmed" mb="md">
        {completedCount} of {requiredActorIds.length} completed ({progressPercent}%)
      </Text>

      {gaps.length === 0 ? (
        <Alert color="green" icon={<IconCheck size={16} />}>
          All required people have completed this item
        </Alert>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Person</Table.Th>
              <Table.Th>Status</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {gaps.map((gap, idx) => (
              <Table.Tr key={idx}>
                <Table.Td>{gap.actorId}</Table.Td>
                <Table.Td>
                  <Badge color="red" variant="light">Not Completed</Badge>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Card>
  );
}

/**
 * Actor completions section - shows completion history for a person
 */
function ActorHistorySection({ actorId }: { actorId: string }) {
  const { data, loading, error, refetch } = useActorCompletionsQuery({
    variables: { actorId, limit: 20 },
  });

  if (loading) return <Loader size="sm" />;
  if (error) return <Alert color="red" title="Error">{error.message}</Alert>;

  const completions = data?.actorCompletions ?? [];

  return (
    <Card withBorder padding="md">
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="green">
            <IconCheck size={14} />
          </ThemeIcon>
          <Text fw={500}>Completion History</Text>
        </Group>
        <Group gap="xs">
          <Badge color="green" variant="light">{completions.length}</Badge>
          <Tooltip label="Refresh">
            <ActionIcon variant="subtle" onClick={() => refetch()}>
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {completions.length === 0 ? (
        <Text c="dimmed" size="sm">No completions recorded</Text>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Item</Table.Th>
              <Table.Th>Completed</Table.Th>
              <Table.Th>Outcome</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {completions.map((c, idx) => (
              <Table.Tr key={idx}>
                <Table.Td>{c.templateRef}</Table.Td>
                <Table.Td>{new Date(c.completedAt).toLocaleDateString()}</Table.Td>
                <Table.Td>
                  <Badge
                    color={c.outcome === 'Completed' ? 'green' : 'yellow'}
                    variant="light"
                  >
                    {c.outcome ?? 'Completed'}
                  </Badge>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Card>
  );
}

/**
 * Main dashboard component
 */
export function TrainingComplianceDashboard() {
  const [activeTab, setActiveTab] = useState<string | null>('overview');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [selectedActor, setSelectedActor] = useState<string>('');
  const [expirationDays, setExpirationDays] = useState<string>('30');

  // TODO: Load from backend settings via useTerminology() or tenant config
  // For now, use placeholder values - in production these come from the backend
  const sampleTemplates = [
    { id: 'tpl-safety-001', name: 'Safety Orientation' },
    { id: 'tpl-compliance-001', name: 'Annual Compliance' },
    { id: 'tpl-equipment-001', name: 'Equipment Certification' },
  ];

  const sampleActors = [
    { id: '2_1', name: 'Staff Member 1' },
    { id: '2_2', name: 'Staff Member 2' },
    { id: '2_3', name: 'Staff Member 3' },
  ];

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Group gap="xs">
            <ThemeIcon size="lg" variant="light" color="blue">
              <IconCertificate size={20} />
            </ThemeIcon>
            <Title order={2}>Completion Tracking</Title>
          </Group>
          <Badge size="lg" variant="light" color="blue">
            DRR-221
          </Badge>
        </Group>

        {/* Info alert */}
        <Alert color="blue" icon={<IconChartBar size={16} />} title="Domain-Agnostic Tracking">
          <Text size="sm">
            This dashboard tracks completion of any required items: training modules, certifications,
            compliance courses, or credentials. Labels adapt to your industry via tenant settings.
          </Text>
        </Alert>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconChartBar size={14} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="expiring" leftSection={<IconClock size={14} />}>
              Expiring
            </Tabs.Tab>
            <Tabs.Tab value="gaps" leftSection={<IconUsers size={14} />}>
              Gaps
            </Tabs.Tab>
            <Tabs.Tab value="history" leftSection={<IconCheck size={14} />}>
              History
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="md">
            <Stack gap="md">
              <Text c="dimmed">
                Select a tab to view expiring credentials, completion gaps, or individual history.
              </Text>

              {/* Quick stats placeholder */}
              <Group grow>
                <Card withBorder padding="md">
                  <Text size="xs" c="dimmed" tt="uppercase">Tracked Items</Text>
                  <Text size="xl" fw={700}>{sampleTemplates.length}</Text>
                </Card>
                <Card withBorder padding="md">
                  <Text size="xs" c="dimmed" tt="uppercase">People</Text>
                  <Text size="xl" fw={700}>{sampleActors.length}</Text>
                </Card>
                <Card withBorder padding="md">
                  <Text size="xs" c="dimmed" tt="uppercase">Queries Available</Text>
                  <Text size="xl" fw={700}>9</Text>
                </Card>
              </Group>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="expiring" pt="md">
            <Stack gap="md">
              <Group>
                <Text fw={500}>Expiration Window:</Text>
                <SegmentedControl
                  value={expirationDays}
                  onChange={setExpirationDays}
                  data={[
                    { label: '7 days', value: '7' },
                    { label: '30 days', value: '30' },
                    { label: '60 days', value: '60' },
                    { label: '90 days', value: '90' },
                  ]}
                />
              </Group>

              {sampleTemplates.map(tpl => (
                <ExpiringSection
                  key={tpl.id}
                  templateRef={tpl.id}
                  withinDays={parseInt(expirationDays)}
                />
              ))}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="gaps" pt="md">
            <Stack gap="md">
              <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
                Select a requirement to see who still needs to complete it.
                In production, required actors are loaded from role assignments.
              </Alert>

              {sampleTemplates.map(tpl => (
                <GapsSection
                  key={tpl.id}
                  templateRef={tpl.id}
                  requiredActorIds={sampleActors.map(a => a.id)}
                />
              ))}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="history" pt="md">
            <Stack gap="md">
              <Alert color="blue" icon={<IconUsers size={16} />}>
                Select a person to see their completion history.
              </Alert>

              {sampleActors.map(actor => (
                <ActorHistorySection key={actor.id} actorId={actor.id} />
              ))}
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}
